import React, { 
  useState, 
  useCallback, 
  useMemo,
  useRef, 
  useEffect
} from "react";

import { 
  useReactTable, 
  getCoreRowModel, 
  flexRender 
} from '@tanstack/react-table';

import { DEFAULT_VALUES } from "../../../../components/components.constants";

import { createDecimalFormatter, createPercentFormatter, textFormatter } from "../../../../utils/formatter/formatter";

const LABEL_HIERARQUIA = textFormatter("hierarquia", { textTransform: 'uppercase' });
const LABEL_ATRIBUTOS = textFormatter("atributos", { textTransform: 'uppercase' });

export const scoreColor = (score) => {
  if (score >= 8) return { color: "#2ea043", bg: "rgba(46,160,67,0.08)", border: "rgba(46,160,67,0.25)" };
  if (score >= 5) return { color: "#d97706", bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.25)" };
  return { color: "#dc2626", bg: "rgba(220,38,38,0.08)", border: "rgba(220,38,38,0.25)" };
};

export const scoreLabel = (score) => {
  if (score >= 8) return "Alto desempenho";
  if (score >= 5) return "Desempenho moderado";
  return "Desempenho crítico";
};

// filtro lateral
export const FilterSection = ({ actions, ctx, uiState }) => {

  const [openGroups, setOpenGroups] = useState({});

  const toggle = useCallback(
    (key) => setOpenGroups(p => ({ ...p, [key]: !p[key] })),
  [])

  const { setFilters, clearFilters } = actions;  
  const { filterOptions, stepTitle = "Filtro de Tipo", label = "tipos" } = ctx;
  const { filters, total } = uiState;

  const hierKeys = useMemo(() => {
    return Object.keys(filterOptions?.hierarquia || {})
  }, [filterOptions]);

  const attrKeys = useMemo(() => {
    return Object.keys(filterOptions?.atributos ?? {})
   }, [filterOptions])

  const activeTags = Object.entries(filters).filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : true));

  const handleSelect = useCallback((key, val) => {
    setFilters(prev => {
      const current = Array.isArray(prev[key]) ? prev[key] : (prev[key] ? [prev[key]] : []);
      const updated = current.includes(val) ? current.filter(v => v !== val) : [...current, val];
      return { ...prev, [key]: updated.length > 0 ? updated : null };
    });
  }, [setFilters]);

  const renderGroup = useMemo(() => (key, options) => {
    const selected = filters[key] || null;
    const open = !!openGroups[key];

    return (
      <div key={key} style={{ borderBottom: "1px solid #f5f5f5" }}>
        <button onClick={() => toggle(key)} style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "9px 14px", background: "none", border: "none", cursor: "pointer",
          fontSize: "11px", fontWeight: "600", color: "#555", letterSpacing: "0.06em" }}>
          {textFormatter(key, { textTransform: 'uppercase' })}
          <span style={{ 
            fontSize: "11px", color: "#bbb", display: "inline-block", 
            transform: open ? "rotate(180deg)" : "none", 
            transition: "transform 0.2s" }}>
              ▾
            </span>
        </button>

        {open && (
          <div style={{ paddingBottom: "6px" }}>
            {options.map(opt => {
              const isSelected = Array.isArray(selected) ? selected.includes(opt.label) : selected === opt.label;
              return (
                <button key={opt.label} onClick={() => handleSelect(key, opt.label)} style={{
                  width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "5px 14px 5px 22px",
                  background: isSelected ? "#fff6f0" : "none", border: "none", cursor: "pointer",
                  fontSize: "12px", color: isSelected ? "#E8773A" : "#444",
                  fontWeight: isSelected ? "600" : "400", textAlign: "left",
                }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {isSelected && 
                      <span style={{ 
                        width: 5, height: 5, borderRadius: "50%", backgroundColor: "#E8773A", display: "inline-block", flexShrink: 0 }} />
                    }
                    {opt.label}
                  </span>
                  <span style={{ color: "#ccc", fontSize: "11px" }}>{opt.count}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }, [filters, openGroups, toggle, handleSelect]);

  return (
    <section style={{
      backgroundColor: "#fff", border: "1px solid #e5e5e5", borderRadius: "12px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden",
      display: "flex", flexDirection: "column", height: "calc(100vh - 280px)"
    }}>

      <div 
        style={{
          padding: "12px 16px", display: "flex", alignItems: "center",
          justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
          flexShrink: 0, backgroundColor: "#fff",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span 
              style={{
                inlineSize: 26, blockSize: 26, borderRadius: "50%",
                backgroundColor: "#E8773A", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: "700", fontSize: "13px", flexShrink: 0,
              }}>
                1
            </span>
            <span style={{ fontWeight: "600", fontSize: "14px", color: "#1a1a1a" }}>{stepTitle}</span>
          </div>
      </div>

      <div style={{ overflowY: "auto", flex: 1 }}>
        <div style={{ padding: "11px 14px 8px" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: activeTags.length > 0 ? 8 : 0 }}>
            <span style={{ fontSize: "20px", fontWeight: "700", color: "#1a1a1a" }}>{total}</span>
            <span style={{ fontSize: "12px", color: "#aaa" }}>/ {total} {label}</span>
            {activeTags.length > 0 && (
              <button 
                onClick={clearFilters} 
                style={{ 
                  marginLeft: "auto", 
                  fontSize: "11px", 
                  color: "#E8773A", 
                  background: "none", 
                  border: "none", 
                  cursor: "pointer", 
                  fontWeight: "600" }}>
                    Limpar filtros
              </button>
            )}
          </div>

          {activeTags.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {activeTags.map(([key, val]) => {
                const vals = Array.isArray(val) ? val : [val];
                return vals.map(v => (
                  <span key={`${key}-${v}`} style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "2px 8px 2px 10px", borderRadius: "20px",
                    backgroundColor: "#fff6f0", border: "1px solid #f4c4a6",
                    fontSize: "11px", color: "#E8773A", fontWeight: "600",
                  }}>
                    {v}
                    <button 
                      onClick={() => handleSelect(key, v)} 
                      style={{ 
                        background: "none", 
                        border: "none", 
                        cursor: "pointer", 
                        color: "#E8773A", 
                        fontSize: "12px", 
                        lineHeight: 1, 
                        padding: 0 }}>
                          ×
                    </button>
                  </span>
                ));
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "4px 14px 4px", fontSize: "10px", fontWeight: "700", color: "#bbb", letterSpacing: "0.1em" }}>
          {LABEL_HIERARQUIA}
        </div>
        {hierKeys.map(key => renderGroup(key, filterOptions.hierarquia[key]))}

        <div style={{ padding: "10px 14px 4px", fontSize: "10px", fontWeight: "700", color: "#bbb", letterSpacing: "0.1em" }}>
          {LABEL_ATRIBUTOS}
        </div>
        {attrKeys.map(key => renderGroup(key, filterOptions.atributos[key]))}
      </div>
    </section>
  );
};

// seleção de itens nos cads
const SelectionItem = ({ actions, ctx, uiState }) => {

  const { onSelect } = actions || {};
  const { item } = ctx || {};
  const { isSelected, mode = 'historical_mode' } = uiState || {};
  
  const scoreStyle = scoreColor(item.score);

  return (
    <button onClick={() => onSelect(item)} style={{
      width: "100%", display: "flex", alignItems: "center", gap: 10,
      padding: "9px 10px", marginBottom: "3px",
      background: isSelected ? "#fff6f0" : "#fff",
      border: isSelected ? "1.5px solid #E8773A" : "1.5px solid #efefef",
      borderRadius: "8px", cursor: "pointer", textAlign: "left",
    }}>

      {/* score e nota do item */}
      <p style={{
        minWidth: 50, height: 30, borderRadius: "7px",
        border: `1.5px solid ${scoreStyle.border}`, backgroundColor: scoreStyle.bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: "12px", fontWeight: "700", color: scoreStyle.color, flexShrink: 0,
      }}>
        {createDecimalFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.score)}
      </p>

      {/* identificação do item e código */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: "13px", fontWeight: "600", color: "#1a1a1a" }}>
          {mode === 'performance_mode' ? (item.levels?.level8 ?? item.name) : (item.name ?? item.code)}
        </p>

        <p style={{ fontSize: "11px", color: "#aaa", marginTop: 1 }}>
          {mode === 'performance_mode' ? `${item.name} · ${item.path}` : item.path}
        </p>
      </div>

      {mode === 'performance_mode' && (() => {
        const count = (item.similarity ?? []).filter(s => s.seasonId === item.seasonId).length;
        return count > 0 && (
          <div style={{
            width: 19, height: 19, borderRadius: "50%",
            backgroundColor: "#E8773A", color: "#fff",
            fontSize: "10px", fontWeight: "700",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            {count}
          </div>
        );
      })()}
    </button>
  );
};

export const SelectionSection = ({ actions, ctx, uiState }) => {

  const { onSelect, onToggleSimilars } = actions || {};
  const { items = [], title, step = 2, isSelected } = ctx || {};
  const { selectedItem, mode } = uiState || {};

  const [sortAsc, setSortAsc] = useState(false);
  const [localOnlyWithSimilars, setLocalOnlyWithSimilars] = useState(false);

  const filtered = useMemo(() =>
    localOnlyWithSimilars
      ? items.filter(item => item.similarity?.some(s => s.seasonId === item.seasonId))
      : items,
    [items, localOnlyWithSimilars]
  );

  const sorted = useMemo(() =>
    [...filtered].sort((a, b) => sortAsc ? a.score - b.score : b.score - a.score),
    [filtered, sortAsc]
  );

  return (
    <section style={{ 
      backgroundColor: "#fff", border: "1px solid #e5e5e5", borderRadius: "12px", 
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)", overflow: "hidden", 
      display: "flex", flexDirection: "column", height: "calc(100vh - 280px)"
    }}>
      <div style={{
        padding: "12px 16px", display: "flex", alignItems: "center",
        justifyContent: "space-between", borderBottom: "1px solid #f0f0f0",
        flexShrink: 0, backgroundColor: "#fff",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{
            inlineSize: 26, blockSize: 26, borderRadius: "50%",
            backgroundColor: "#E8773A", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: "700", fontSize: "13px", flexShrink: 0,
          }}>{step}</span>
          <span style={{ fontWeight: "600", fontSize: "14px", color: "#1a1a1a" }}>{title}</span>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onToggleSimilars && (
            <button
              title={localOnlyWithSimilars ? "Todos os produtos" : "Apenas com similares"}
              onClick={() => {
                setLocalOnlyWithSimilars(p => !p);
                onToggleSimilars(p => !p);
              }}
              style={{
                width: 30, height: 30, borderRadius: "50%",
                backgroundColor: localOnlyWithSimilars ? "#E8773A" : "#fff",
                border: `1.5px solid ${localOnlyWithSimilars ? "#E8773A" : "#e0e0e0"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", transition: "all 0.15s",
                color: localOnlyWithSimilars ? "#fff" : "#888",
              }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <circle cx="9" cy="7" r="4" />
                <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                <path d="M21 21v-2a4 4 0 0 0-3-3.87" />
              </svg>
            </button>
          )}

          <button onClick={() => setSortAsc(p => !p)} title={sortAsc ? "Menor nota" : "Maior nota"} style={{
            width: 30, height: 30, borderRadius: "50%",
            backgroundColor: "#E8773A", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", flexShrink: 0, color: "#fff", fontSize: "14px",
            transition: "opacity 0.15s",
          }}>
            {sortAsc ? "↑" : "↓"}
          </button>
        </div>
      </div>

      <div style={{ overflowY: "auto", flex: 1, padding: "6px 8px" }}>
        {sorted.map((item) => (
          <SelectionItem
            actions={{ onSelect: onSelect }}
            ctx={{ item: item }}
            uiState={{ isSelected: isSelected(item, selectedItem), mode: mode }}
            key={
              `${item.attributePerformanceMatrixAggregationId}-${item.levels.level8}-${item.productId}` ??
              `${item.attributePerformanceMatrixId}-${item.productId}-${item.name}-${item.path}-${item.levels?.level5}-${item.levels?.level6}-${item.levels?.level7}`
            }/>
        ))}
      </div>
    </section>
  );
};

// navegação interna das abas
export const getStepCols = (showStep2, showStep3) => {
  switch (true) {
    case showStep3:
      return "230px 270px 1fr";
    case showStep2:
      return "230px 270px";
    default:
      return "230px";
  }
};

// tabelas
export const ComparativeTable = ({ data }) => {

  const groupedBySeason = useMemo(() => {
    const groups = {};
    data.forEach(row => {
      const key = row.seasonId;
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    Object.values(groups).forEach(rows =>
      rows.sort((a, b) => (a.isSimilar ? 1 : -1))
    );
    return Object.entries(groups).sort(([a], [b]) => Number(b) - Number(a));
  }, [data]);

  const rowBg = (row) => {
    if (row.isSimilar) return "#fafafa";
    if (row.level8) return "#fafafa";
    return "#fff6f0";
  };

  const fmtPct = v => v == null
    ? <span style={{ color: "#bbb", fontWeight: "500" }}>—</span>
    : createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);

  const fmtScore = (pastScore, estimatedScore) => {
    const v = pastScore || estimatedScore;
    return v
      ? <strong style={{ color: scoreColor(v).color }}>{v.toFixed(2)}</strong>
      : <span style={{ color: "#bbb", fontWeight: "500" }}>—</span>;
  };

  const thStyle = {
    padding: "8px 10px", fontWeight: "600", color: "#888",
    fontSize: "11px", whiteSpace: "nowrap", borderBottom: "2px solid #f0f0f0"
  };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>Ano</th>
            <th style={{ ...thStyle, textAlign: "left" }}>Produto</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Clientes que Compram</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Part. Mix</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Part. Lucro Marca</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Part. na Venda (pçs)</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Part. na Venda ($)</th>
            <th style={{ ...thStyle, textAlign: "center" }}>Nota</th>
          </tr>
        </thead>
        <tbody>
          {groupedBySeason.map(([seasonId, rows]) =>
            rows.map((row, idx) => (
              <tr key={`${seasonId}-${idx}`} style={{
                borderBottom: "1px solid #f0f0f0",
                backgroundColor: rowBg(row),
              }}>
                {idx === 0 && (
                  <td rowSpan={rows.length} style={{
                    padding: "9px 10px", fontWeight: "700", color: "#333",
                    verticalAlign: "middle", borderRight: "1px solid #f0f0f0",
                    whiteSpace: "nowrap",
                    backgroundColor: rowBg(rows[0]),
                  }}>
                    {row.year ?? '—'}
                    <div style={{ fontSize: "10px", color: "#E8773A", fontWeight: "600", marginTop: 2 }}>
                      {row.seasonName}
                    </div>
                  </td>
                )}
                <td style={{ padding: "9px 10px", color: "#444" }}>
                  {row.level8 && (
                    <div style={{
                      fontSize: "10px",
                      color: row.isSimilar ? "#E8773A" : "#666",
                      marginTop: 2
                    }}>
                      {row.level8}
                    </div>
                  )}
                </td>
                <td style={{ padding: "9px 10px", textAlign: "center" }}>{fmtPct(row.purchaseAffinity)}</td>
                <td style={{ padding: "9px 10px", textAlign: "center" }}>{fmtPct(row.assortmentShare)}</td>
                <td style={{ padding: "9px 10px", textAlign: "center" }}>{fmtPct(row.profitShare)}</td>
                <td style={{ padding: "9px 10px", textAlign: "center" }}>{fmtPct(row.quantitySalesShare)}</td>
                <td style={{ padding: "9px 10px", textAlign: "center" }}>{fmtPct(row.revenueShare)}</td>
                <td style={{ padding: "9px 10px", textAlign: "center" }}>{fmtScore(row.pastScore, row.estimatedScore)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div style={{ marginTop: 10, display: "flex", gap: 16, fontSize: "11px", color: "#888" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2ea043", display: "inline-block", flexShrink: 0 }} />
          Vendas &gt; Ocupação
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#dc2626", display: "inline-block", flexShrink: 0 }} />
          Vendas &lt; Ocupação
        </span>
      </div>

    </div>
  );
};

export const IndicatorTable = ({ data }) => {
  const columns = useMemo(() => [
    {
      accessorKey: 'period',
      header: 'Ano',
      cell: ({ row }) => (
        <span style={{ fontWeight: "600", color: "#333" }}>
          {row.original.period ?? row.original.year ?? '—'}
        </span>
      ),
    },
    {
      accessorKey: 'purchaseAffinity',
      header: 'Clientes que Compram',
      cell: ({ getValue }) => {
        const value = getValue();
        return value == null
          ? <span style={{ color: "#333" }}>—</span>
          : createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      },
    },
    {
      accessorKey: 'assortmentShare',
      header: 'Part. Mix',
      cell: ({ getValue }) => {
        const value = getValue();
        return value == null
          ? <span style={{ color: "#333" }}>—</span>
          : createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      },
    },
    {
      accessorKey: 'profitShare',
      header: 'Part. Lucro Marca',
      cell: ({ getValue }) => {
        const value = getValue();
        return value == null
          ? <span style={{ color: "#333" }}>—</span>
          : createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      },
    },
    {
      accessorKey: 'quantitySalesShare',
      header: 'Part. na Venda (pçs)',
      cell: ({ getValue }) => {
        const value = getValue();
        return value == null
          ? <span style={{ color: "#333" }}>—</span>
          : createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      },
    },
    {
      accessorKey: 'revenueShare',
      header: 'Part. na Venda ($)',
      cell: ({ getValue }) => {
        const value = getValue();
        return value == null
          ? <span style={{ color: "#333" }}>—</span>
          : createPercentFormatter({ minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
      },
    },
    {
      accessorKey: 'pastScore',
      header: 'Nota',
      cell: ({ row }) => {
        const value = row?.original?.pastScore || row?.original.estimatedScore
        return value
          ? <strong style={{ color: scoreColor(value).color }}>{value.toFixed(2)}</strong>
          : <span style={{ color: "#ccc" }}>{DEFAULT_VALUES.NOT_APPLY}</span>;
      },
    },
  ], []);

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <table style={{ inlineSize: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id} style={{ borderBottom: "2px solid #f0f0f0" }}>
            {headerGroup.headers.map((header, i) => (
              <th key={header.id} style={{
                padding: "8px 10px",
                textAlign: i === 0 ? "left" : "center",
                fontWeight: "600", color: "#888", fontSize: "11px", whiteSpace: "nowrap"
              }}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map(row => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell, i) => (
              <td key={cell.id} style={{
                padding: "9px 10px",
                textAlign: i === 0 ? "left" : "center",
                color: "#555"
              }}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// seletores
const pulseStyleId = "sales-select-pulse-style";

export const AssortmentSelect = React.memo(({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Todos",
  width = "fit-content",
  disabled = false,
  searchable = false,
}) => {

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (document.getElementById(pulseStyleId)) return;
    const style = document.createElement("style");
    style.id = pulseStyleId;
    style.textContent = `
      @keyframes AssortmentSelectPulse {
        0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.55); }
        70% { box-shadow: 0 0 0 5px rgba(22, 163, 74, 0); }
        100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filterBySearch = (opts) => searchable
    ? opts.filter(opt => opt.label?.toString().toLowerCase().includes(search.toLowerCase()))
    : opts;

  const filteredOngoing = filterBySearch(
    [...options].filter((opt) => !!opt.isCurrentSellingSeason)
  );
  const filteredFinished = filterBySearch(
    [...options].filter((opt) => !opt.isCurrentSellingSeason)
  );

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (optValue) => {
    onChange(optValue);
    setOpen(false);
    setSearch("");
  };

  const renderSection = (title, opts, isOngoing = false) => {
    if (opts.length === 0) return null;
    return (
      <section>
        <p style={{
          fontSize: "10px", fontWeight: "700", color: "#aaa",
          letterSpacing: "0.04em", padding: "6px 14px 4px",
          textTransform: "uppercase",
        }}>
          {title}
        </p>
        {opts.map(opt => {
          const isActive = opt?.value === value;
          return (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt.value)}
              disabled={disabled}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                width: "100%", padding: "7px 14px", cursor: disabled ? "not-allowed" : "pointer",
                fontSize: "13px", color: isActive ? "#E8773A" : "#333",
                fontWeight: isActive ? "700" : "400", textAlign: "left",
                background: isActive ? "#fff6f0" : "none", border: "none",
                whiteSpace: "normal",
              }}>
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                backgroundColor: isOngoing ? "#16a34a" : (isActive ? "#E8773A" : "#d0d0d0"),
                display: "inline-block", flexShrink: 0,
                animation: isOngoing ? "AssortmentSelectPulse 1.6s infinite" : "none",
              }} />
              {opt.label}
            </button>
          );
        })}
      </section>
    );
  };

  return (
    <div ref={rootRef} style={{ position: "relative", inlineSize: width }}>
      <button
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "7px 12px", border: `1px solid ${open ? "#E8773A" : "#ddd"}`,
          borderRadius: "8px", background: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: "13px", color: "#333", whiteSpace: "nowrap",
          minWidth: "120px", justifyContent: "space-between",
          opacity: disabled ? 0.6 : 1,
        }}>
        <span>{displayText}</span>
        <svg
          width={12} height={12} viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <polyline points="6,9 12,15 18,9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          background: "#fff", border: "1px solid #e0e0e0", borderRadius: "10px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 100, inlineSize: "fit-content",
          padding: "8px 0", maxHeight: "300px", overflowY: "auto", minWidth: "260px",
        }}>
          {searchable && (
            <section style={{ padding: "0 12px 8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #eee", borderRadius: "6px", padding: "5px 8px" }}>
                <svg
                  width={13} height={13} viewBox="0 0 24 24" fill="none"
                  stroke="#E8773A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar..."
                  style={{ border: "none", outline: "none", fontSize: "12.5px", width: "100%", background: "none" }}
                  autoFocus
                />
              </div>
            </section>
          )}

          {renderSection("Vendas em Andamento", filteredOngoing, true)}

          {filteredOngoing.length > 0 && filteredFinished.length > 0 && (
            <div style={{ borderTop: "1px solid #f0f0f0", margin: "4px 0" }} />
          )}

          {renderSection("Vendas Finalizadas", filteredFinished, false)}

          {filteredOngoing.length === 0 && filteredFinished.length === 0 && (
            <p style={{ padding: "10px 14px", fontSize: "12.5px", color: "#aaa" }}>
              Nenhum resultado
            </p>
          )}
        </div>
      )}
    </div>
  );
});